import { MenuItem, Select } from "@mui/material";
import { type WorkerCredentialConfiguration } from "@src/appV2/FacilityProfile/api/useUpdateWorkerCredentialConfiguration";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { SettingRow } from "@src/appV2/redesign/components/SettingRow";

import { SettingNumberInput } from "../../components/SettingNumberInput";
import { SettingToggle } from "../../components/SettingToggle";
import { type SettingsPermissions } from "../../types";

type CredentialVariant = "pin-only" | "pin-plus-password";

interface WorkerCredentialsSectionViewProps {
  configuration: WorkerCredentialConfiguration;
  isUpdating: boolean;
  permissions: SettingsPermissions;
  onChartingToggle: (checked: boolean) => void;
  onTimekeepingToggle: (checked: boolean) => void;
  onOnboardingViewToggle: (checked: boolean) => void;
  onShiftStartingSoonThresholdChange: (value: number) => void;
  onCredentialVariantChange: (variant: CredentialVariant) => void;
}

export function WorkerCredentialsSectionView({
  configuration,
  isUpdating,
  permissions,
  onChartingToggle,
  onTimekeepingToggle,
  onOnboardingViewToggle,
  onShiftStartingSoonThresholdChange,
  onCredentialVariantChange,
}: WorkerCredentialsSectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const isFacilityOrCbhAdmin = permissions.isFacilityAdmin || permissions.isAdmin;
  const isReadOnly = !permissions.isAdmin;
  const isDisabled = isUpdating || isReadOnly;

  // Match `getCredentialVariant` semantics: the contract's `optionalEnumWithFallback`
  // parses unknown stored values to the `"UNRECOGNIZED_"` sentinel, which must resolve
  // to "pin-only" rather than leaving the Select with an out-of-range value.
  const { credentialVariant } = configuration.timekeeping;
  const selectedCredentialVariant: CredentialVariant =
    credentialVariant === "pin-plus-password" ? "pin-plus-password" : "pin-only";

  return (
    <>
      {isFacilityOrCbhAdmin && (
        <>
          <SettingToggle
            checked={configuration.charting.enabled}
            disabled={isDisabled}
            subtitle={tu(
              "When enabled, workplace users will be able to share and manage charting credentials on behalf of workers."
            )}
            title={tu("Charting")}
            onChange={onChartingToggle}
          />

          <SettingToggle
            checked={configuration.timekeeping.enabled}
            disabled={isDisabled}
            subtitle={tu(
              "When enabled, workplace users will be able to share and manage timekeeping credentials on behalf of workers."
            )}
            title={tu("Timekeeping")}
            onChange={onTimekeepingToggle}
          />

          {permissions.isAdmin && configuration.timekeeping.enabled && (
            <SettingRow
              title={tu("Credential Variant")}
              subtitle={tu("Choose which credentials workers need to provide for timekeeping.")}
              disabled={isDisabled}
              action={
                <Select
                  size="small"
                  value={selectedCredentialVariant}
                  disabled={isDisabled}
                  inputProps={{ "aria-label": tu("Credential Variant") }}
                  sx={{ minWidth: "12.5rem" }}
                  onChange={(event) => {
                    onCredentialVariantChange(event.target.value as CredentialVariant);
                  }}
                >
                  <MenuItem value="pin-only">{tu("Pin only")}</MenuItem>
                  <MenuItem value="pin-plus-password">{tu("Pin + Password")}</MenuItem>
                </Select>
              }
            />
          )}

          <SettingToggle
            checked={configuration.onboardingView.enabled}
            disabled={isDisabled}
            subtitle={tu(
              "When enabled, shows professionals who are missing credentials and have upcoming shifts, so credentials can be shared before their shift starts."
            )}
            title={tu("Onboarding View")}
            onChange={onOnboardingViewToggle}
          />

          {configuration.onboardingView.enabled && (
            <SettingNumberInput
              disabled={isDisabled}
              min={1}
              subtitle={tu(
                "Number of hours before a shift starts to consider it 'starting soon' and highlight professionals who still need credentials."
              )}
              title={tu("Shift Starting Soon Threshold")}
              unit={tu("hours")}
              value={configuration.onboardingView.shiftStartingSoonThreshold.value}
              onChange={onShiftStartingSoonThresholdChange}
            />
          )}
        </>
      )}
    </>
  );
}
